<template>
    <div
        v-tooltip="showTooltip ? text : ''"
        class="stf-chip-button"
        :class="{
            'stf-chip-button--selected': modelValue,
            'stf-chip-button--full': full
        }"
        @click="handleClick"
    >
        <div class="stf-chip-button__wrapper" :class="{
            'stf-chip-button__wrapper--selectable': selectable,
        }">
            <StfIcon
                v-if="selectable"
                :icon="modelValue ? 'squareCheck' : 'squareSolid'"
                :color="modelValue ? 'green' : 'white'"
            />
            <StfBody
                class="stf-chip-label" ref="labelRef"
                :class="{
                    'stf-chip-label--selected': modelValue,
                    'stf-chip-label--bold': bold
                }"
            >{{ text }}</StfBody>
            <StfIcon
                v-if="closable"
                icon="xMark"
                class="stf-chip-close"
                @click="onClickClose"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import StfIcon from '@/ui/icon/StfIcon.vue';
import {ref, onMounted, watch, nextTick, onBeforeUnmount, useTemplateRef, type ComponentInstance, computed} from 'vue';
import StfBody from '@/ui/typography/StfBody.vue';
import useMobile from '@/composables/useMobileDependable';

interface Props {
    text?: string;
    modelValue?: boolean;
    selectable?: boolean;
    closable?: boolean;
    full?: boolean;
    bold?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    text: '',
    modelValue: false,
    selectable: false,
    closable: false,
    full: false,
    bold: false
});

const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void;
    (e: 'close'): void;
}>();

const { isDesktop } = useMobile();
const labelRef = useTemplateRef<ComponentInstance<typeof StfBody> | null>('labelRef');
const isTruncated = ref(false);

const checkTruncation = () => {
    if (labelRef.value) {
        const element = labelRef.value.$el;
        isTruncated.value = element.scrollWidth > element.clientWidth;
    }
};

onMounted(() => {
    checkTruncation();
    window.addEventListener('resize', checkTruncation);
});

// Clean up event listener
onBeforeUnmount(() => {
    window.removeEventListener('resize', checkTruncation);
});

// Re-check truncation when text changes
watch(() => props.text, () => {
    nextTick(checkTruncation);
});

const showTooltip = computed(() => {
    if (!isDesktop) {
        return false;
    }

    return isTruncated.value;
});

const handleClick = () => {
    if (props.selectable) {
        emit('update:modelValue', !props.modelValue);
    }
};

const onClickClose = () => {
    emit('close');
};
</script>

<style scoped lang="scss">
@use "bulma/sass/utilities/mixins";
@use '@/assets/sass/_variables' as vars;

.stf-chip-button {
  border-radius: 4px;
  background-color: vars.$color-gray-570;
  border: none;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &__wrapper {
    display: flex;
    padding: 7px 15px;
    align-items: center;
    gap: 18px;
    overflow: hidden;
    justify-content: start;
    flex-wrap: nowrap;
    text-align: left;
    font-family: inherit;

    &--selectable {
      cursor: pointer;

      &:hover {
        opacity:0.8;
      }
    }
  }

  &--selected {
    background-color: rgba(vars.$color-success, 0.1);
  }

  &--full {
    width: 100%;
  }
}

.stf-chip-label {
  align-self: stretch;
  margin-top: auto;
  margin-bottom: auto;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  color: vars.$color-white;

  @include mixins.mobile {
    white-space: normal;
    word-break: break-word;
    overflow-wrap: break-word;
  }

  &--selected {
    color: vars.$color-black;
  }

  &--bold {
    font-weight: vars.$font-weight-semibold;
  }
}

.stf-chip-close {
  cursor: pointer;
  flex-shrink: 0;
}
</style>
